<template>
	<div class="w-100 h-100">
		<b-container fluid>
			<h2 class="tw-mb-0 text-aegis-dk">VHT Arm Product Distribution Data Upload</h2>
			<p class="tw-italic tw-text-gray-700">Bulk Upload VHT Upload Data</p>
		</b-container>

		<hr />

		<b-card no-body>
			<b-tabs card fill>
				<!-- show All Data -->
				<b-tab
					:title="'VHT Records'"
					@click="refresh_all_records_table"
					active
				>
					<b-card-text>
						<b-row>
							<b-col>
								<small>
									Total Results: {{ vhtTotalRows }} 
									<i 
										@click="downloadVhtCsv"
										class="fa fa-download text-success"
										v-b-modal.report-modal
										style="margin-left: 10px;
										cursor: pointer;"
										title="Download All"
									></i>
								</small>
							</b-col>
						</b-row>

						<div style="width: 100%; overflow: auto;">
							<b-table
								ref="view_all_records"
								striped
								hover
								selectable
								select-mode='single'
								@row-selected="onRowSelected"
								:fields="vht_fields"
								:items="getRecords"
								:current-page="vhtCurrentPage"
								:per-page="vhtPerPage"
								filter="All"
								:busy.sync="vhtTableBusy"
								tbody-tr-class="vht-table-cell"
								thead-class="vht-thead-class"
							>
								<!-- Replace rows with a busy spinner while the table data is loading. -->
								<!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
								<template #table-busy>
									<div class="text-danger my-2">
										<b-spinner></b-spinner>
										<strong>&nbsp;Loading...</strong>
									</div>
								</template>
							</b-table>
							<b-row>
								<b-col cols="2">
									<b-form-group id="fieldset-2" label="Results Per Page" label-for="input-2">
										<b-form-select id="input-2" v-model="vhtPerPage" :options="perPageOptions"></b-form-select>
									</b-form-group>
								</b-col>
								<b-col class="my-1" cols="9" style="padding-top: 30px;">
									<b-pagination
										v-model="vhtCurrentPage"
										:total-rows="vhtTotalRows"
										:per-page="vhtPerPage"
										align="fill"
										size="sm"
										class="my-0"
									></b-pagination>
								</b-col>
							</b-row>
						</div>
					</b-card-text>
				</b-tab>
				<b-tab
					:title="'Batch Uploads'"
					@click="refresh_all_files_table"
				>
					<b-card-text>
						<b-container>
							<b-row>
								<b-col>
									<b-button
										variant="secondary"
										size="sm"
										class="ml-1"
										@click="downloadTemplate"
									>
										Download Template
									</b-button>
								</b-col>
							</b-row>
							<!-- file upload form -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-form-file
										id="file"
										v-model="selectedFile"
										:state="Boolean(selectedFile)"
										placeholder="Choose a file or drop it here..."
										drop-placeholder="Drop file here..."
										size="lg"
										accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
									></b-form-file>
								</b-col>
							</b-row>

							<!-- alert if uploaded file is incorrect -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-alert
										v-if="uploading_error"
										show
										dismissible
										variant="danger"
									>
										Found error(s). Please check the data file and reupload.
										<span v-if="error_msg">
											{{ error_msg }}
										</span>
									</b-alert>
								</b-col>
							</b-row>

							<!-- Upload button -->
							<b-row>
								<b-col md="6" offset-md="3" class="mt-3">
									<b-button
										@click="onUpload"
										variant="success"
										size="lg"
										block
										:disabled="uploading"
									>
										{{ uploading ? 'Uploading...' : 'Upload' }}
										<b-spinner
											v-if="uploading"
											small
										></b-spinner>
									</b-button>
								</b-col>
							</b-row>
						</b-container>

						<hr />

						<!-- search filename -->
						<!-- <b-container fluid>
							<b-row>
								<b-col>
									<b-form-input
										v-model="search.upload"
										placeholder="Search Filename"
										size="lg"
										@keyup="getFiles"
									></b-form-input>
								</b-col>
							</b-row>
						</b-container> -->

						<hr>
						<b-row>
							<b-col>
								<small>
									Total Results: {{ fileTotalRows }} 
									<!-- <i 
										@click="downloadFile"
										class="fa fa-download text-success"
										v-b-modal.report-modal
										style="margin-left: 10px;
										cursor: pointer;"
										title="Download All"
									></i> -->
								</small>
							</b-col>
						</b-row>
						<div style="width: 100%; overflow: auto;">
							<b-table
								ref="fileTable"
								striped
								hover
								selectable
								select-mode='single'
								@row-selected="onRowSelected"
								borderless
								:fields="vht_file_fields"
								:items="getFiles"
								:current-page="filesCurrentPage"
								:per-page="filesPerPage"
								:tbody-tr-class="'clickable vht-table-cell'"
								thead-class="vht-thead-class"
								:busy.sync="fileTableBusy"
							>
								<!-- Replace rows with a busy spinner while the table data is loading. -->
								<!-- https://bootstrap-vue.org/docs/components/table#table-busy-state -->
								<template #table-busy>
									<div class="text-danger my-2">
										<b-spinner></b-spinner>
										<strong>&nbsp;Loading...</strong>
									</div>
								</template>
							</b-table>
						</div>
					</b-card-text>
				</b-tab>
			</b-tabs>
		</b-card>

		<!-- modal view for accepted and rejected records -->
		<b-modal
			id="file-records-modal"
			size="lg"
			scrollable
			:title="set_modal_title"
		>
			<b-row>
				<b-col>
					<small>
						Total Results: {{ modalTotalRows }} 
						<i 
							@click="downloadFileRecordsCsv"
							class="fa fa-download text-success"
							v-b-modal.report-modal
							style="margin-left: 10px;
							cursor: pointer;"
							title="Download File Records"
						></i>
					</small>
				</b-col>
			</b-row>
            <b-table
                responsive
                :items="cptItems"
				:fields="modal_fields"
				tbody-tr-class="vht-table-cell"
				thead-class="vht-thead-class"
            />
			<template #modal-footer>
				<!-- <b-button variant="danger" @click="deleteVhtFile">Delete File</b-button> -->
				<b-button
					class="float-left mr-3"
					variant="danger"
					@click="onDelete"
				>Delete File</b-button>
				<b-button class="float-right" @click="hideModal('file-records-modal')">Close</b-button>
			</template>
        </b-modal>
		<b-modal
			id="delete-file-modal"
			v-model="deleteFileModalShow"
			centered
			scrollable
			no-close-on-backdrop
			title="Confirm Upload File Deletion"
			ok-title="Yes, Delete"
			@show="resetModal"
			@hidden="resetModal"
			@ok="handleOk"
			:header-bg-variant="'danger'"
			:header-text-variant="'light'">
			<span v-html="'Are you sure you want to delete this upload file and all associated VHT records?'"></span>
			<form ref="form" @submit.stop.prevent="handleSubmit">
				<b-form-group
					invalid-feedback="Confirmation is required"
					:state="deleteConfirmState"
				>
				<br>
				<b-form-checkbox
					id="delete-confirm-input"
					name="delete-confirm-chkbx"
					v-model="deleteConfirmed"
				>I'm sure.</b-form-checkbox>
				</b-form-group>
			</form>
		</b-modal>
	</div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";
import { mapGetters } from "vuex";

export default {
	mixins: [DashboardBaseTemplate],
	data() {
		return {
			selectedFile: null,  // file to be uploaded
			file_title: "",
			search: {category: "vht"},
            modal_fields: [
				{ key: 'id', label: "ID" },
				{ key: 'household_id', label: "Household ID" },
				{ key: 'cluster_number', label: "Cluster Number" },
				{ key: 'date', label: "Date" },
				{ key: 'total_sr_distributed', label: "Total SR Distributed" },
				{ key: 'total_sr_hanging_at_start', label: "Total SR Hanging at Start" },
				{ key: 'number_sr_incorrectly_placed', label: "Number SR Incorrectly Placed" },
				{ key: 'number_sr_retrieved', label: "Number SR Retrieved" },
				{ key: 'number_sr_missing', label: "Number SR Missing" },
				{ key: 'reasons_for_missing_sr', label: "Reasons for Missing SR" },
				{ key: 'actions_taken', label: "Actions Taken" },
				{ key: 'accepted', label: "Accepted" },
				{ key: 'message', label: "Message" }
			],
            modal_items: [],
			modalTotalRows: 0,
            // modal_fields: [],
			// check accepted or rejected for modal table's title
			uploading: false,
			uploading_error: false,
			error_msg: null,
			fileTotalRows: 0,
			filesCurrentPage: 1,
			filesPerPage: 20,
            vht_file_fields: [
				{ key: 'id', label: "ID" },
				{ key: 'title', label: "Title" },
				{ key: 'uploaded_time', label: "Uploaded" },
				{ key: 'uploaded_by', label: "Uploaded By" },
				{ key: 'accepted', label: "Accepted Records" },
				{ key: 'rejected', label: "Rejected Records" },
			],
            vht_fields: [
				{ key: 'id', label: "ID" },
				{ key: 'household_id', label: "Household ID" },
				{ key: 'cluster_number', label: "Cluster Number" },
				{ key: 'date', label: "Date" },
				{ key: 'total_sr_distributed', label: "Total SR Distributed" },
				{ key: 'total_sr_hanging_at_start', label: "Total SR Hanging at Start" },
				{ key: 'number_sr_incorrectly_placed', label: "Number SR Incorrectly Placed" },
				{ key: 'number_sr_retrieved', label: "Number SR Retrieved" },
				{ key: 'number_sr_missing', label: "Number SR Missing" },
				{ key: 'reasons_for_missing_sr', label: "Reasons for Missing SR" },
				{ key: 'actions_taken', label: "Actions Taken" },
				{ key: 'upload_file', label: "Upload ID" },
				{ key: 'upload_file_name', label: "Upload File" },
				// { key: 'accepted', label: "Accepted" },
				// { key: 'message', label: "Message" }
			],
			vhtTotalRows: 0,
            vhtPerPage: 50,
            vhtCurrentPage: 1,
			perPageOptions: [
				10,
				15,
				20,
				25,
				50
			],
			fileTableBusy: false,
			vhtTableBusy: false,
			csvDownloading: false,
			deleteConfirmed: '',
			deleteConfirmState: null,
			deleteFileModalShow: false,
		};
	},
	computed: {
		...mapGetters("user", ['inMyGroups']),
		set_modal_title() {
			return this.file_title + " record(s)";
		},
		cptItems(){
			// add formatting to the row items based on the value in the RowUploadStatus column
			return this.modal_items.map((item)=>{
				let tmp=item;
				if (item.accepted == true){
					tmp._rowVariant='success';
				} else {
					tmp._rowVariant='danger';
				}
				return tmp;
			})  
		}
	},
	created() {},
	mounted() {},
	methods: {
		getFiles: function(ctx) {
			// hide modal
			// this.$bvModal.hide('file-records-modal');
			const that = this;
			var params = new URLSearchParams();
			params.append("current_page", ctx.currentPage);
			params.append("per_page", ctx.perPage);
			return HTTP.get("/uganda/view-vht-files", {params: params})
				.then((response) => {
					that.fileTotalRows = response.data.total_rows;
					return response.data.row_data;
				})
				.catch((error) => {
					HandleErrors.HandleErrors(error);
				});
		},
		// for hiding modal
        beforeRouteLeave(to, from, next) {
            this.$bvModal.hide('file-records-modal');
            next();
        },
		onUpload: function () {
			const that = this;

			if (!that.selectedFile) {
				return that.$bvModal.msgBoxOk("Please select a file!", {
                    title: "File Error",
                    okVariant: "danger",
                    centered: true,
				});
			}

			// set initialize uploading_error to be false before upload
			that.uploading_error = false;
			that.error_msg = null;

			const fd = new FormData();
			fd.append("file", that.selectedFile);
			fd.append("title", that.selectedFile.name);

			HTTP.post("/uganda/upload-vht-records", fd).then(() => {
                that.selectedFile = null;
                that.$notify({
                    text: "File successfully uploaded!",
                    duration: 10000,
                    type: "success",
                });

                that.selectedFile = null;
                that.title = "";
                that.uploading_error = false;
				that.error_msg = null;
                that.refresh_all_files_table();
            }).catch((error) => {
                // raise alert if uploading error occurs
                that.uploading_error = true;
				that.error_msg = error.response.data.message;
            }).then(() => {
                that.uploading = false;
            });
		},
		// table view
        getRecords: function(ctx){
            const that = this;
            const params = {
                current_page: ctx.currentPage,
                per_page: ctx.perPage,
                    category: that.category,
			};

			// check table is being updated
			that.updating_table = true;

            return HTTP.get("/uganda/view-vht-records", {params: params})
				.then(response => {

                that.vhtTotalRows = response.data.total_rows
                // since getRecords is called at b-table/items, response data should be returned
                return response.data.row_data

            }).catch((error) => {
                HandleErrors.HandleErrors(error);
            });
        },
		downloadVhtCsv: function(){
			var params = new URLSearchParams();
			params.append("return_as", "csv");

			HTTP.get("/uganda/view-vht-records", {params: params}).then(response => {
				var fileURL = window.URL.createObjectURL(
					new Blob([response.data])
				);
				var fileLink = document.createElement("a");
				fileLink.href = fileURL;
				fileLink.setAttribute("download", "VHTRecords.csv");
				document.body.appendChild(fileLink);
				fileLink.click();
			});
		},
		downloadFileRecordsCsv: function(){
			let that = this;
			var params = new URLSearchParams();
			params.append("return_as", "csv");
			params.append("file_id", that.file_id);

			HTTP.get("/uganda/view-vht-file-records", {params: params}).then(response => {
				var fileURL = window.URL.createObjectURL(
					new Blob([response.data])
				);
				var fileLink = document.createElement("a");
				fileLink.href = fileURL;
				fileLink.setAttribute("download", that.file_title + ".csv");
				document.body.appendChild(fileLink);
				fileLink.click();
			});
		},
		onDelete() {
			let that = this;
			that.hideModal('file-records-modal');
			that.deleteFileModalShow = true
		},
		deleteVhtFile: function(){
			let that = this;
			// that.hideModal('file-records-modal');
			const fd = new FormData();
			fd.append("file_id", that.file_id);

			that.uploading_error = false;

			return HTTP.post("/uganda/archive-vht-file", fd).then(() => {
				that.$notify({
					text: "File successfully deleted!",
					duration: 10000,
					type: "success",
			});
				that.refresh_all_files_table();
                that.selectedFile = null;
                that.title = "";
				
			}).catch((error) => {
                // raise alert if uploading error occurs
                that.uploading_error = true;
				that.error_msg = error.response.data.message;
			});
		},
		downloadTemplate(){
			var params = new URLSearchParams();
			var request = {
				params: params,
				responseType: "arraybuffer",
			};
	
			HTTP.get(`/uganda/download-vht-template`, request)
			.then((response) => {
				var fileURL = window.URL.createObjectURL(
					new Blob([response.data])
				);
				var fileLink = document.createElement("a");
				fileLink.href = fileURL;
				var fileName = "VHTUploadTemplate.xlsx";
				fileLink.setAttribute("download", fileName);
				document.body.appendChild(fileLink);
				fileLink.click();
			})
			.catch((error) => {
				this.err_msg = HandleErrors.HandleErrors(error);
				this.apiErrModalShow = true;
			});
		},
        refresh_all_records_table: function () {
            this.$refs.view_all_records.refresh();
        },
        refresh_all_files_table: function () {
            this.$refs.fileTable.refresh();
        },
		onRowSelected(items) {
			if(this.inMyGroups(['VHT Manager'])) {
				const that = this;
				// this.$router.push({name: 'vht-edit', params: {id: items[0]['id'] }});
				var params = new URLSearchParams();
				that.file_title = items[0]['title'];
				that.file_id = items[0]['id'];
				params.append("file_id", that.file_id);
				that.$bvModal.show('file-records-modal');
				return HTTP.get("/uganda/view-vht-file-records", {params: params})
				.then(response => {
					that.modalTotalRows = response.data.total_rows;
					that.modal_items =  response.data.row_data;
				}).catch((error) => {
					HandleErrors.HandleErrors(error);
				});
			} else {
				console.log('Not authorized to edit records.')
			}
		},
		resetModal() {
			this.deleteConfirmed = ''
			this.deleteConfirmState = null
		},
		handleOk(bvModalEvt) {
			// Prevent modal from closing
			bvModalEvt.preventDefault()
			// Trigger submit handler
			this.handleSubmit()
		},
		handleSubmit() {
			// Exit when the form isn't valid
			if (!this.deleteConfirmed) {
				this.deleteConfirmState = false;
				return;
			}
			// Hide the modal manually
			this.deleteFileModalShow = false;
			this.deleteVhtFile();
		},
		onOk() {
			// Handle OK action
		},
		onCancel() {
			// Handle Cancel action
		},
		customAction() {
			// Handle custom action
		},
		hideModal(modal_id) {
			console.log("Hiding modal: ", modal_id);
			this.$bvModal.hide(modal_id);
		}
	},
};
</script>
<style>
.vht-table-cell {
	white-space: nowrap;
    margin: auto !important;
    padding: 3px !important;
    font-size: small !important;
    max-width: 100px;
}
.vht-thead-class th {
  font-size: small !important;
}
</style>